import PaymentModal from '@/components/PaymentModal';
import React, { useEffect, useRef, useState } from 'react';
import {
  Form,
  Button,
  Dropdown,
  Space,
  Modal,
  message,
  Checkbox,
  InputNumber,
  Typography,
} from 'antd';
import type { CheckboxProps, MenuProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { requestOrderPayment } from '@/services/plan';
import { getAgreementsEnable } from '@/services/agreement';
import { DownOutlined } from '@ant-design/icons';
import {useReactive} from "ahooks";
import {getTimePlusSeconds} from "@/utils/util";
import { useModel, useNavigate } from '@umijs/max';
import usePayTypeMenuItems, { formatPayTypeName } from '@/hooks/usePayTypeMenuItems';
import usePaymentPolling from '@/hooks/usePaymentPolling';
import usePaymentRequest from '@/hooks/usePaymentRequest';
import usePaymentRedirect from '@/hooks/usePaymentRedirect';
import { PayType } from '@/types/payment';
import type { OrderPaymentParams } from '@/types/payment';

interface RechargeFormValues {
  price: number;
}

const RechargePage = () => {
  const navigate = useNavigate();
  const { initialState, refresh } = useModel('@@initialState');
  const [form] = Form.useForm<RechargeFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const paymentRedirect = usePaymentRedirect();

  const [isModalOpen1, setIsModalOpen1] = useState(false); //二维码支付弹窗显示
  const [isModalVisible, setIsModalVisible] = useState(false); //点击红色按钮显示弹窗
  const [isAgreeModalVisible, setAgreeIsModalVisible] = useState(false); //协议弹窗
  const activeAddressTypeRef = useRef<PayType | null>(null);

  useEffect(() => () => {
    activeAddressTypeRef.current = null;
  }, []);

  const state = useReactive({
    // 购买get返回值
    getPayValue:{
      amount: 0 as number | string,
      timeout: 0,
      address: '',
      endTime:'',
    },
    // 购买类型（狗狗币，支付宝等）
    selectedKey:-1,
    check:false,
  });

  const resetPayState = () => {
    state.getPayValue = {
      amount: 0,
      timeout: 0,
      address: '',
      endTime:'',
    }
  };

  // 支付类型
  const { items } = usePayTypeMenuItems({
    filter: (item) => formatPayTypeName(item?.payTypeName) !== '余额支付',
  }); // 过滤掉 "余额支付"

  // 获取协议
  const getAgreementsEnableRes = useQuery({
    queryKey: ['getAgreementsEnable'],
    queryFn: getAgreementsEnable,
  });

  // 请求标识只用于前端校验，接口仍接收原有支付参数。
  const rechargeRes = usePaymentRequest({
    mutationFn: (params: OrderPaymentParams, context) => requestOrderPayment(params, context),
    onSuccess: (res, params) => {
      if ([PayType.Alipay, PayType.Card, PayType.Blockchain].includes(params.addressType)) {
        paymentRedirect.openPaymentPage(res?.data);
        return;
      }
      const { amount, address, remainingTime } = typeof res?.data === 'object' && res.data ? res.data : {};
      if (!address || amount === undefined) {
        messageApi.warning('未获取到支付信息，请重试');
        return;
      }
      const timeout = remainingTime ?? 0;
      // 更新支付数据 & 倒计时
      state.selectedKey = params.addressType;
      state.getPayValue = {
        amount,
        address ,
        endTime:getTimePlusSeconds(timeout),
        timeout,
      }
      postPayInfoRes.startSession();
      setIsModalOpen1(true);
    },
  });
  const isSubmitting = rechargeRes.isPending;

  // 其他类型支付post
  const postPayInfoRes = usePaymentPolling({
    scope: 'recharge',
    params: {
      address: state.getPayValue.address,
      addressType: state.selectedKey,
      amount: state.getPayValue.amount,
      userId: initialState?.user?.id,
      pacType: 2,
      reNew: false,
    },
    enabled: isModalOpen1 && state.getPayValue?.timeout > 0,
    refetchInterval : 15000,
    onFinish: (message, isSuccess) => {
      closeModal();
      const requestId = rechargeRes.getRequestId();
      if (isSuccess) refresh();
      messageApi.open({
        type: isSuccess ? 'success' : 'error',
        content: message,
        duration: 2,
        onClose: () => {
          if (!rechargeRes.isCurrentRequest(requestId) || !postPayInfoRes.isCurrentSession()) return;
          navigate('/planManager/historyOrder');
        },
      });
    },
  });

  const showAgree = ()=>{
    setAgreeIsModalVisible(true);
  }

  // 协议弹窗消失
  const closeAgree = ()=>{
    setAgreeIsModalVisible(false);
  }

  // 是否选中协议
  const onChange: CheckboxProps['onChange'] = (e)=>{
    state.check = e.target.checked
  }

  // 点击红色充值按钮
  const handleRecharge: MenuProps['onClick'] = (info) => {
    setIsModalClose();
    const addressType = Number(info.key);
    activeAddressTypeRef.current = addressType;
    setIsModalVisible(true);
    state.selectedKey = addressType;
  };

  // 点击蓝色充值按钮调接口
  const handleRechargeEvery = (totalAmount: number) => {
    const addressType = activeAddressTypeRef.current;
    if (addressType === null) return;

    const commonParams: OrderPaymentParams = {
      addressType,
      totalAmount: totalAmount?.toString(),
      userId: initialState?.user?.id,
      pacType: 2,
      reNew: false,
    };
    rechargeRes.mutate({
        ...commonParams,
        ...([PayType.Alipay, PayType.Card, PayType.Blockchain].includes(addressType) ? {
          currentUrl: window.location.href,
          description: '充值',
          orderName: '充值',
        } : {}),
    });
  };

  // 二维码支付弹窗消失
  const setIsModalClose = () => {
    paymentRedirect.reset();
    postPayInfoRes.stopSession();
    rechargeRes.reset();
    setIsModalOpen1(false);
    resetPayState();
  };

  // 点击红色按钮显示弹窗消失
  const closeModal = () => {
    setIsModalClose();
    activeAddressTypeRef.current = null;
    setIsModalVisible(false);
    form.resetFields();
    state.selectedKey = -1;
    state.check = false;
  };

  // 点击蓝色重置按钮表单提交
  const onFinish = (values: RechargeFormValues) => {
    if(!state.check){
      messageApi.warning("请先同意支付协议")
      return
    }
    handleRechargeEvery(Number(values?.price));
  };
  return (
      <>
        {contextHolder}
        {paymentRedirect.contextHolder}
        {initialState?.user?.admin !== 1 && (
            <Dropdown menu={{ items, onClick: handleRecharge }}>
              <Button type="primary" danger>
                <Space>
                  充值
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
        )}
        {/* 支付 Modal */}
        <PaymentModal
          open={isModalOpen1}
          addressType={state.selectedKey}
          amount={state.getPayValue.amount}
          address={state.getPayValue.address}
          endTime={state.getPayValue.endTime}
          onClose={setIsModalClose}
          purpose="充值"
        />

        {/* 弹框 */}
        <Modal
            title="充值"
            open={isModalVisible}
            onCancel={closeModal}
            footer={null} // 隐藏默认按钮
            width={440}
            centered
        >
          <div style={{ paddingTop: 12 }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              padding: '14px 16px',
              marginBottom: 24,
              background: '#f7f8fa',
              borderRadius: 8,
            }}>
              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                <Typography.Text type="secondary">充值账号</Typography.Text>
                <div style={{ marginTop: 4, fontWeight: 600, overflowWrap: 'anywhere' }}>
                  {initialState?.user?.username}
                </div>
              </div>
              <div>
                <Typography.Text type="secondary">手机号</Typography.Text>
                <div style={{ marginTop: 4 }}>{initialState?.user?.phone || '--'}</div>
              </div>
            </div>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item
                  label="充值金额（美元）"
                  name="price"
                  rules={[{ required: true, message: "请填写充值价格" }]}
              >
                <InputNumber
                    prefix="$"
                    placeholder="请输入充值金额"
                    min={1}
                    max={10000000}
                    size="large"
                    style={{ width: '100%' }}
                    disabled={isSubmitting}
                />
              </Form.Item>
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24 }}>
                <Checkbox checked={state.check} onChange={onChange} style={{ flexShrink: 0 }}>
                  已阅读
                </Checkbox>
                <Typography.Link onClick={showAgree} style={{ overflowWrap: 'anywhere' }}>
                  《{getAgreementsEnableRes.data?.data?.title || '支付协议'}》
                </Typography.Link>
              </div>
              <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={isSubmitting}
                  disabled={isSubmitting}
              >
                确认充值
              </Button>
            </Form>
          </div>
        </Modal>

        <Modal
            title="支付协议"
            open={isAgreeModalVisible}
            onCancel={closeAgree}
            footer={null} // 隐藏默认按钮
            width={800}
        >
          <div dangerouslySetInnerHTML={{ __html: getAgreementsEnableRes.data?.data?.content || "" }}></div>
        </Modal>
      </>
  )

};

export default RechargePage;
